// frontend/src/pages/PaychequeList/usePaycheques.ts
// React Query hooks for the paycheque list page.
// Each hook is reusable from any component that needs the same data.

import { useQuery } from "@tanstack/react-query";
import apiFetch from "../../utils/apiFetch";
import { authHeaders } from "./helpers";
import type { Paycheque, CompanySettings } from "./types";

/**
 * Fetches all paycheques for the current org.
 * Handles both legacy response shapes: a raw array, or { paycheques: [...] } / { items: [...] }.
 */
export function usePaycheques() {
  return useQuery<Paycheque[]>({
    queryKey: ["paycheques"],
    queryFn: async function () {
      const res = await apiFetch("/api/v1/payroll/paycheques", {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Could not load paycheques");
      const data = await res.json();
      return Array.isArray(data)
        ? data
        : (data.paycheques || data.items || []);
    },
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetches the current company's payroll settings (name + address).
 * Returns null on non-OK responses (settings are optional).
 */
export function useCompanySettings() {
  return useQuery<CompanySettings | null>({
    queryKey: ["company-settings"],
    queryFn: async function () {
      const res = await apiFetch("/api/v1/payroll/settings", {
        headers: authHeaders(),
      });
      if (!res.ok) return null;
      return await res.json();
    },
    refetchOnWindowFocus: false,
  });
}